"use client";

import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { SymbolAvatar } from "@/components/common";
import {
  MoreOptionsIcon,
  PlusIcon,
  EyeIcon,
  EyeOffIcon,
} from "@/components/icons";
import { useChartSettingsStore } from "@/lib/chart-settings";
import {
  calculateTradeAccountPnl,
  formatSignedPnl,
  getTradeClosePrice,
  getTradePriceChange,
  useOpenedTrades,
  VybeOrderType,
} from "@/vybe-trader-client/opened-trades";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useIsInvestorAccount } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";
import { InvestorBadge, InvestorReadOnlyNotice } from "@/components/common/InvestorBadge";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades";
import { formatOrderType } from "@/vybe-trader-client/opened-trades";
import { useChartStore } from "@/vybe-trader-client/chart/stores/useChartStore";
import { useSettingsStore } from "@/lib/settings";
import TradeOrderScreen from "../trade-order/TradeOrderScreen";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
} from "@/shadcn-components/ui/drawer";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import CloseByPositionDialog from "@/components/trades-view/close-by/CloseByPositionDialog";
import { resolveCloseLots } from "@/lib/terminal/engine/shared/orders";
import { AccountMetricsRows } from "@/components/account-information";

const OpenPositionDetails = lazy(() => import("./components/OpenPositionDetails"));
const ModifyPositionScreen = lazy(
  () => import("../modify-position/ModifyPositionScreen"),
);
const MarketDepthScreen = lazy(
  () => import("../market-depth/MarketDepthScreen"),
);
interface Position {
  symbol: string;
  type: "buy" | "sell";
  volume: number;
  openPrice: number;
  currentPrice: number;
  profit: number;
  ticket?: number;
  swap?: number;
  openTime?: Date | string;
  stopLoss?: number;
  takeProfit?: number;
  comment?: string;
  orderType?: number;
}

interface TradeScreenProps {
  onNavigateToChart?: (symbol: string) => void;
  shouldOpenTradeOrder?: boolean;
  /** Symbol the auto-opened trade ticket targets (the row the user tapped). Takes precedence over the
   *  legacy chart-store selection, which is only ever seeded with the first watchlist symbol. */
  tradeOrderSymbol?: string;
  onTradeOrderClosed?: () => void;
}

export default function TradeScreen({
  onNavigateToChart,
  shouldOpenTradeOrder,
  tradeOrderSymbol,
  onTradeOrderClosed,
}: TradeScreenProps) {
  // Get selectedSymbol from chart store
  const selectedSymbol = useChartStore((state) => state.selectedSymbol);

  // Get settings store
  const { getSetting, updateSetting } = useSettingsStore();
  const isAdvancedTradesVisible = getSetting("isAdvancedTradesVisible");

  // Get opened trades data
  const { allTrades, loadingStatus } = useOpenedTrades();

  // Get account information
  const accountInfo = useAccountInformationStore((state) => state.account);
  const accountLoadingStatus = useAccountInformationStore(
    (state) => state.loadingStatus
  );

  // Get VybeTrader client
  const { client } = useVybeTraderClient();

  const allSubscribedSymbols = useSubscribedSymbolsStore(
    (state) => state.byCurrency
  );
  const allSymbols = useSymbolsStore((state) => state.byCurrency);
  const currency = accountInfo?.currency || "USD";

  const [selectedPosition, setSelectedPosition] = useState<Position | null>(
    null
  );
  const [showPositionDrawer, setShowPositionDrawer] = useState(false);
  const [showTradeOrder, setShowTradeOrder] = useState(false);
  const [showModifyPosition, setShowModifyPosition] = useState(false);
  const [showMarketDepth, setShowMarketDepth] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string>();
  const [modifyingPosition, setModifyingPosition] = useState<any>(null);
  const [marketDepthData, setMarketDepthData] = useState<any>(null);
  const [closingOrders, setClosingOrders] = useState<Set<number>>(new Set());
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [closeByTrade, setCloseByTrade] = useState<OpenedTrade | null>(null);
  // Investor (read-only) login: positions + P&L stay, every trade verb goes.
  const isInvestor = useIsInvestorAccount();
  // Partial-close lots for the position drawer ("" = full volume).
  const [closeLotsInput, setCloseLotsInput] = useState("");

  // Map OpenedTrade to Position interface (deprecated - will be removed)
  const mapTradeToPosition = (trade: OpenedTrade): Position => ({
    symbol: trade.symbol,
    type: trade.orderType === VybeOrderType.Buy ? "buy" : "sell",
    volume: trade.lots,
    openPrice: trade.openPrice,
    currentPrice: getTradeClosePrice(
      trade,
      allSubscribedSymbols.get(trade.symbol)?.quote
    ),
    profit: calculateTradeAccountPnl({
      trade,
      symbol: allSymbols.get(trade.symbol),
      quote: allSubscribedSymbols.get(trade.symbol)?.quote,
      accountCurrency: currency,
      symbols: allSymbols,
      quotes: allSubscribedSymbols,
    }),
    ticket: trade.ticket,
    swap: trade.swap,
    openTime: trade.openTime,
    stopLoss: trade.stopLoss,
    takeProfit: trade.takeProfit,
    comment: trade.comment,
    orderType: trade.orderType,
  });

  const positions: Position[] = allTrades.map(mapTradeToPosition);

  // Calculate live total profit from all trades with useMemo for reactivity
  const liveTotalProfit = useMemo(() => {
    return allTrades.reduce((sum, trade) => {
      const subscribedSymbol = allSubscribedSymbols.get(trade.symbol);
      return (
        sum +
        calculateTradeAccountPnl({
          trade,
          symbol: allSymbols.get(trade.symbol),
          quote: subscribedSymbol?.quote,
          accountCurrency: currency,
          symbols: allSymbols,
          quotes: allSubscribedSymbols,
        })
      );
    }, 0);
  }, [allTrades, allSubscribedSymbols, allSymbols, currency]);

  const displayProfit = liveTotalProfit;

  // Automatically open TradeOrderScreen when shouldOpenTradeOrder is true
  useEffect(() => {
    // Prefer the explicitly requested symbol (the tapped row); the legacy store is a last resort.
    const target = tradeOrderSymbol || selectedSymbol?.currency;
    if (shouldOpenTradeOrder && target) {
      setTradeSymbol(target);
      setShowTradeOrder(true);
      // Notify parent that we've handled the trade order opening
      if (onTradeOrderClosed) {
        onTradeOrderClosed();
      }
    }
  }, [shouldOpenTradeOrder, tradeOrderSymbol, selectedSymbol, onTradeOrderClosed]);

  const handlePositionClick = (position: Position) => {
    setSelectedPosition(position);
    setCloseLotsInput("");
    setShowPositionDrawer(true);
  };

  const handleClosePosition = async () => {
    if (!selectedPosition?.ticket) {
      console.error("No ticket number for position");
      setSelectedPosition(null);
      setShowPositionDrawer(false);
      return;
    }

    if (!client || !client.isConnected()) {
      console.error("Client not connected");
      setSelectedPosition(null);
      setShowPositionDrawer(false);
      return;
    }

    const ticket = selectedPosition.ticket;
    setClosingOrders((prev) => new Set(prev).add(ticket));

    try {
      const trade = allTrades.find((openTrade) => openTrade.ticket === ticket);
      const quote = useSubscribedSymbolsStore
        .getState()
        .byCurrency.get(selectedPosition.symbol)?.quote;

      const fullLots = trade?.lots ?? selectedPosition.volume;
      const requestedLots = parseFloat(closeLotsInput);
      const volumeStep = useSymbolsStore
        .getState()
        .findSymbolByCurrency(selectedPosition.symbol)?.volumeStep;
      const closeLots = trade
        ? resolveCloseLots(trade, requestedLots, volumeStep)
        : Number.isFinite(requestedLots) &&
            requestedLots > 0 &&
            requestedLots < fullLots
          ? requestedLots
          : fullLots;

      const closeData = {
        Ticket: ticket,
        Symbol: selectedPosition.symbol,
        Price: trade
          ? getTradeClosePrice(trade, quote)
          : selectedPosition.currentPrice,
        Lots: closeLots,
        OrderType: trade?.orderType ?? selectedPosition.orderType ?? 0,
        Slippage: 100,
      };

      await client.closeOrder(closeData);
    } catch (error) {
      console.error("Error closing position:", error);
    } finally {
      setClosingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(ticket);
        return newSet;
      });
      setSelectedPosition(null);
      setShowPositionDrawer(false);
    }
  };

  const handleTrade = () => {
    if (selectedPosition) {
      setTradeSymbol(selectedPosition.symbol);
      setSelectedPosition(null);
      setShowPositionDrawer(false);
      setShowTradeOrder(true);
    }
  };

  const handleCloseByHedge = () => {
    if (!selectedPosition?.ticket) return;

    const trade = allTrades.find(
      (openTrade) => openTrade.ticket === selectedPosition.ticket
    );

    if (!trade) return;

    setCloseByTrade(trade);
    setSelectedPosition(null);
    setShowPositionDrawer(false);
  };

  const handleDepthOfMarket = () => {
    if (selectedPosition) {
      setMarketDepthData({
        positionId: selectedPosition.ticket?.toString() || "0",
        symbol: selectedPosition.symbol,
        currentVolume: selectedPosition.volume,
      });
      setSelectedPosition(null);
      setShowPositionDrawer(false);
      setShowMarketDepth(true);
    }
  };

  const handleChart = () => {
    if (selectedPosition && onNavigateToChart) {
      onNavigateToChart(selectedPosition.symbol);
      setSelectedPosition(null);
      setShowPositionDrawer(false);
    }
  };

  const handleBulkOperations = () => {
    setSelectedPosition(null);
    setShowPositionDrawer(false);
  };

  const handleModify = async (
    stopLoss: number | null,
    takeProfit: number | null
  ) => {
    if (!modifyingPosition || !modifyingPosition.ticket) {
      console.error("No position data available for modification");
      setShowModifyPosition(false);
      return;
    }

    if (!client || !client.isConnected()) {
      console.error("Client not connected");
      setShowModifyPosition(false);
      return;
    }

    try {
      const orderType =
        modifyingPosition.orderType ??
        (modifyingPosition.type === "buy"
          ? VybeOrderType.Buy
          : VybeOrderType.Sell);

      const modifyData = {
        Ticket: modifyingPosition.ticket,
        Symbol: modifyingPosition.symbol,
        Lots: modifyingPosition.volume,
        Price: modifyingPosition.openPrice,
        OrderType: orderType,
        StopLoss: stopLoss || 0,
        TakeProfit: takeProfit || 0,
        // "Remove stop loss / take profit" on the modify screen: 0 alone means "not specified" on the
        // wire, so an explicit removal of a level the position has needs its own flag.
        ClearStopLoss: !stopLoss && (modifyingPosition.stopLoss ?? 0) > 0,
        ClearTakeProfit: !takeProfit && (modifyingPosition.takeProfit ?? 0) > 0,
        Comment: "Modified via MT6 Trading App",
      };

      await client.modifyOrder(modifyData);
    } catch (error) {
      console.error("Error modifying position:", error);
    } finally {
      setShowModifyPosition(false);
      setModifyingPosition(null);
    }
  };

  const handleNewPosition = () => {
    setTradeSymbol(selectedSymbol?.currency || "");
    setShowTradeOrder(true);
  };

  const toggleAdvancedView = () => {
    updateSetting("isAdvancedTradesVisible", !isAdvancedTradesVisible);
  };

  const handleMoreOptions = () => {
    // Only show bulk operations if there are trades
    if (allTrades.length > 0) {
      setShowBulkOperations(true);
    }
  };

  const calculateLiveProfit = (trade: OpenedTrade) => {
    const subscribedSymbol = allSubscribedSymbols.get(trade.symbol);
    return calculateTradeAccountPnl({
      trade,
      symbol: allSymbols.get(trade.symbol),
      quote: subscribedSymbol?.quote,
      accountCurrency: currency,
      symbols: allSymbols,
      quotes: allSubscribedSymbols,
    });
  };

  // Helper function to close a single position
  const closeSinglePosition = async (trade: OpenedTrade, lots?: number) => {
    if (!client || !client.isConnected()) {
      console.error("Client not connected");
      return false;
    }

    const ticket = trade.ticket;
    setClosingOrders((prev) => new Set(prev).add(ticket));

    try {
      const quote = useSubscribedSymbolsStore
        .getState()
        .byCurrency.get(trade.symbol)?.quote;

      const closeData = {
        Ticket: ticket,
        Symbol: trade.symbol,
        Price: getTradeClosePrice(trade, quote),
        Lots: lots && lots > 0 ? Math.min(lots, trade.lots) : trade.lots,
        OrderType: trade.orderType,
        Slippage: 100,
      };

      await client.closeOrder(closeData);
      return true;
    } catch (error) {
      console.error("Error closing position:", error);
      return false;
    } finally {
      setClosingOrders((prev) => {
        const newSet = new Set(prev);
        newSet.delete(ticket);
        return newSet;
      });
    }
  };

  const handleCloseAllPositions = async () => {
    setShowBulkOperations(false);

    if (allTrades.length === 0) {
      return;
    }

    // Close all positions simultaneously using Promise.all
    const closePromises = allTrades.map((trade) => closeSinglePosition(trade));

    try {
      await Promise.all(closePromises);
    } catch (error) {
      console.error("Error closing some positions:", error);
    }
  };

  const handleCloseProfitablePositions = async () => {
    setShowBulkOperations(false);

    // Filter profitable positions
    const profitablePositions = allTrades.filter((trade) => {
      const liveProfit = calculateLiveProfit(trade);
      return liveProfit > 0;
    });

    if (profitablePositions.length === 0) {
      return;
    }

    // Close profitable positions simultaneously using Promise.all
    const closePromises = profitablePositions.map((trade) =>
      closeSinglePosition(trade)
    );

    try {
      await Promise.all(closePromises);
    } catch (error) {
      console.error("Error closing some profitable positions:", error);
    }
  };

  const handleCloseLosingPositions = async () => {
    setShowBulkOperations(false);

    // Filter losing positions
    const losingPositions = allTrades.filter((trade) => {
      const liveProfit = calculateLiveProfit(trade);
      return liveProfit < 0;
    });

    if (losingPositions.length === 0) {
      return;
    }

    // Close losing positions simultaneously using Promise.all
    const closePromises = losingPositions.map((trade) =>
      closeSinglePosition(trade)
    );

    try {
      await Promise.all(closePromises);
    } catch (error) {
      console.error("Error closing some losing positions:", error);
    }
  };

  const handleCancelBulkOperations = () => {
    setShowBulkOperations(false);
  };

  const handleMarketDepthSell = (volume: number) => {
    setShowMarketDepth(false);
  };

  const handleMarketDepthBuy = (volume: number) => {
    setShowMarketDepth(false);
  };

  const handleMarketDepthClose = async (volume: number, positionId?: string) => {
    const trade = allTrades.find(
      (openTrade) => openTrade.ticket.toString() === positionId
    );
    if (trade) {
      await closeSinglePosition(trade, volume);
    }
    setShowMarketDepth(false);
  };

  // Format date time
  const formatDateTime = (dateStr: string) => {
    if (dateStr === "0001-01-01T00:00:00") return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  if (showMarketDepth && marketDepthData) {
    return (
      <Suspense fallback={null}>
        <MarketDepthScreen
          onBack={() => setShowMarketDepth(false)}
          positionId={marketDepthData.positionId}
          symbol={marketDepthData.symbol}
          currentVolume={marketDepthData.currentVolume}
          onSell={handleMarketDepthSell}
          onBuy={handleMarketDepthBuy}
          onClose={handleMarketDepthClose}
        />
      </Suspense>
    );
  }

  if (showModifyPosition && modifyingPosition) {
    // Get current bid/ask prices for the symbol
    const quote = useSubscribedSymbolsStore
      .getState()
      .byCurrency.get(modifyingPosition.symbol)?.quote;
    const currentBid = quote?.bid || modifyingPosition.openPrice;
    const currentAsk = quote?.ask || modifyingPosition.openPrice;

    return (
      <Suspense fallback={null}>
        <ModifyPositionScreen
          key={modifyingPosition?.ticket ?? "none"}
          position={modifyingPosition}
          currentBid={currentBid}
          currentAsk={currentAsk}
          onBack={() => setShowModifyPosition(false)}
          onModify={handleModify}
        />
      </Suspense>
    );
  }

  if (showTradeOrder) {
    return (
      <TradeOrderScreen
        symbol={tradeSymbol}
        onBack={() => setShowTradeOrder(false)}
      />
    );
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* MT5-style mobile trade account header */}
      <div className="border-b border-border bg-background">
        <div className="flex h-11 items-center justify-between px-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleAdvancedView}
            className="h-9 w-9"
          >
            {isAdvancedTradesVisible ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeOffIcon className="h-4 w-4" />
            )}
          </Button>
          <div className="flex-1 text-center">
            <p
              className={`text-xl font-bold ${
                displayProfit >= 0 ? "text-blue-400" : "text-red-400"
              }`}
            >
              {formatSignedPnl(displayProfit)} {currency}
            </p>
          </div>
          {isInvestor ? (
            <span className="h-9 w-9" aria-hidden="true" />
          ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNewPosition}
            className="h-9 w-9"
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
          )}
        </div>

        {isInvestor && (
          <div className="flex justify-center pb-1">
            <InvestorBadge />
          </div>
        )}
        <div className="px-3 pb-2">
          {accountLoadingStatus === "loading" ? (
            <div className="py-2 text-center text-xs text-muted-foreground">
              Loading account information...
            </div>
          ) : accountInfo ? (
            <AccountMetricsRows compact />
          ) : (
            <div className="py-2 text-center text-xs text-muted-foreground">
              No account data available
            </div>
          )}
        </div>
      </div>

      {/* Positions Header - Only show if there are trades */}
      {allTrades.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <h2 className="text-sm font-medium text-foreground">Positions</h2>
          {!isInvestor && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleMoreOptions}
            className="h-8 w-8"
          >
            <MoreOptionsIcon className="h-4 w-4" />
          </Button>
          )}
        </div>
      )}

      {/* Positions List */}
      <div className="flex-1 overflow-y-auto px-3">
        {loadingStatus === "loading" ? (
          <div className="text-center text-muted-foreground py-4">
            Loading positions...
          </div>
        ) : allTrades.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            No open positions
          </div>
        ) : (
          allTrades.map((trade) => (
            <TradePositionRow
              key={trade.ticket}
              trade={trade}
              isClosing={closingOrders.has(trade.ticket)}
              isAdvancedView={isAdvancedTradesVisible}
              isSelected={selectedPosition?.ticket === trade.ticket}
              onClick={() => handlePositionClick(mapTradeToPosition(trade))}
            />
          ))
        )}
      </div>

      {/* Bulk Operations Modal */}
      {showBulkOperations && (
        <Drawer open={showBulkOperations} onOpenChange={setShowBulkOperations}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="text-xl font-semibold">
                Bulk Operations
              </DrawerTitle>
              <DrawerDescription>
                Choose an action to apply to multiple positions
              </DrawerDescription>
            </DrawerHeader>

            <div className="px-4 pb-6 space-y-3">
              {/* Always show Close All if there are any positions */}
              {allTrades.length > 0 && (
                <Button
                  onClick={handleCloseAllPositions}
                  variant="outline"
                  className="w-full h-12 text-blue-400 border-blue-400/20 hover:bg-blue-400/10"
                >
                  Close All Positions ({allTrades.length})
                </Button>
              )}

              {/* Show Close Profitable only if there are profitable positions */}
              {(() => {
                const profitableCount = allTrades.filter((trade) => {
                  const liveProfit = calculateLiveProfit(trade);
                  return liveProfit > 0.00000001; // Use same tolerance as display
                }).length;

                return profitableCount > 0 ? (
                  <Button
                    onClick={handleCloseProfitablePositions}
                    variant="outline"
                    className="w-full h-12 text-blue-400 border-blue-400/20 hover:bg-blue-400/10"
                  >
                    Close Profitable Positions ({profitableCount})
                  </Button>
                ) : null;
              })()}

              {/* Show Close Losing only if there are losing positions */}
              {(() => {
                const losingCount = allTrades.filter((trade) => {
                  const liveProfit = calculateLiveProfit(trade);
                  return liveProfit < -0.00000001; // Use same tolerance as display
                }).length;

                return losingCount > 0 ? (
                  <Button
                    onClick={handleCloseLosingPositions}
                    variant="outline"
                    className="w-full h-12 text-red-400 border-red-400/20 hover:bg-red-400/10"
                  >
                    Close Losing Positions ({losingCount})
                  </Button>
                ) : null;
              })()}

              <Separator className="my-4" />

              <DrawerClose asChild>
                <Button variant="ghost" className="w-full h-12">
                  Cancel
                </Button>
              </DrawerClose>
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* Position Details Drawer */}
      <Drawer open={showPositionDrawer} onOpenChange={setShowPositionDrawer}>
        <DrawerContent>
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-xl font-semibold">
              Position Details
            </DrawerTitle>
            <DrawerDescription className="text-sm text-muted-foreground">
              {selectedPosition && (
                <>
                  {selectedPosition.symbol} • #{selectedPosition.ticket}
                </>
              )}
            </DrawerDescription>
          </DrawerHeader>

          {selectedPosition && (
            <div className="px-4 pb-6">
              {/* Position Summary */}
              <div className="bg-muted/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        selectedPosition.type === "buy"
                          ? "default"
                          : "secondary"
                      }
                      className={`${
                        selectedPosition.type === "buy"
                          ? "bg-blue-500"
                          : "bg-red-500"
                      } text-white px-3 py-1`}
                    >
                      {selectedPosition.type.toUpperCase()}{" "}
                      {selectedPosition.volume}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-2xl font-bold ${
                        selectedPosition.profit >= 0
                          ? "text-blue-400"
                          : "text-red-400"
                      }`}
                    >
                      {formatSignedPnl(selectedPosition.profit)}
                    </p>
                    <p className="text-sm text-muted-foreground">{currency}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Open Price</p>
                    <p className="font-medium">
                      {selectedPosition.openPrice.toFixed(5)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Current Price</p>
                    <p className="font-medium">
                      {selectedPosition.currentPrice.toFixed(5)}
                    </p>
                  </div>
                  {selectedPosition.stopLoss &&
                    selectedPosition.stopLoss > 0 && (
                      <div>
                        <p className="text-muted-foreground">Stop Loss</p>
                        <p className="font-medium text-red-400">
                          {selectedPosition.stopLoss.toFixed(5)}
                        </p>
                      </div>
                    )}
                  {selectedPosition.takeProfit &&
                    selectedPosition.takeProfit > 0 && (
                      <div>
                        <p className="text-muted-foreground">Take Profit</p>
                        <p className="font-medium text-blue-400">
                          {selectedPosition.takeProfit.toFixed(5)}
                        </p>
                      </div>
                    )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Opened:</span>
                  <span>
                    {formatDateTime(selectedPosition.openTime as string)}
                  </span>
                </div>
                {selectedPosition.swap !== undefined &&
                  selectedPosition.swap !== 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Swap:</span>
                      <span
                        className={
                          selectedPosition.swap >= 0
                            ? "text-blue-400"
                            : "text-red-400"
                        }
                      >
                        {selectedPosition.swap.toFixed(2)}
                      </span>
                    </div>
                  )}
                {selectedPosition.comment && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Comment:</span>
                    <span className="text-right max-w-[60%]">
                      {selectedPosition.comment}
                    </span>
                  </div>
                )}
              </div>

              <Separator className="my-6" />

              {/* Action Buttons */}
              <div className="space-y-3">
                {isInvestor ? (
                  <InvestorReadOnlyNotice comfortable />
                ) : (
                <>
                {/* Partial close: lots to close (empty = full volume) */}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder={`Lots (max ${selectedPosition.volume})`}
                    value={closeLotsInput}
                    onChange={(e) => setCloseLotsInput(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    onClick={() =>
                      setCloseLotsInput((selectedPosition.volume / 2).toFixed(2))
                    }
                  >
                    ½
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 px-3"
                    onClick={() =>
                      setCloseLotsInput((selectedPosition.volume / 4).toFixed(2))
                    }
                  >
                    ¼
                  </Button>
                </div>
                <Button
                  onClick={handleClosePosition}
                  variant="destructive"
                  className="w-full h-12 text-base font-medium"
                  disabled={closingOrders.has(selectedPosition.ticket || 0)}
                >
                  {closingOrders.has(selectedPosition.ticket || 0)
                    ? "Closing..."
                    : "Close Position"}
                </Button>

                <Button
                  onClick={handleCloseByHedge}
                  variant="outline"
                  className="w-full h-12 text-base font-medium border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                  disabled={closingOrders.has(selectedPosition.ticket || 0)}
                >
                  Close By Hedge
                </Button>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => {
                      setShowPositionDrawer(false);
                      setShowModifyPosition(true);
                      setModifyingPosition(selectedPosition);
                    }}
                    variant="outline"
                    className="h-11"
                  >
                    Modify
                  </Button>
                  <Button
                    onClick={handleTrade}
                    variant="outline"
                    className="h-11"
                  >
                    Trade
                  </Button>
                </div>
                </>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleDepthOfMarket}
                    variant="outline"
                    className="h-11"
                  >
                    Market Depth
                  </Button>
                  <Button
                    onClick={handleChart}
                    variant="outline"
                    className="h-11"
                  >
                    Chart
                  </Button>
                </div>

                <Separator className="my-4" />

                <DrawerClose asChild>
                  <Button variant="ghost" className="w-full h-11">
                    Cancel
                  </Button>
                </DrawerClose>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <CloseByPositionDialog
        open={!!closeByTrade}
        onOpenChange={(open) => {
          if (!open) setCloseByTrade(null);
        }}
        position={closeByTrade}
        isMobile
      />
    </div>
  );
}

// TradePositionRow Component
interface TradePositionRowProps {
  trade: OpenedTrade;
  isClosing?: boolean;
  isAdvancedView?: boolean;
  isSelected?: boolean;
  onClick: () => void;
}

function TradePositionRow({
  trade,
  isClosing = false,
  isAdvancedView = true,
  isSelected = false,
  onClick,
}: TradePositionRowProps) {
  const allSubscribedSymbols = useSubscribedSymbolsStore(
    (state) => state.byCurrency
  );
  const allSymbols = useSymbolsStore((state) => state.byCurrency);
  const accountCurrency = useAccountInformationStore(
    (state) => state.account?.currency || "USD"
  );
  const quote = allSubscribedSymbols.get(trade.symbol)?.quote;

  // Calculate live data
  const tradeType = formatOrderType(trade.orderType);
  const isBuyTrade = tradeType === "Buy";

  // Live current price: for buy trades use bid (selling price), for sell trades use ask (buying price)
  const liveCurrentPrice = getTradeClosePrice(trade, quote);
  const priceChange = getTradePriceChange(trade, quote);
  const liveProfit = calculateTradeAccountPnl({
    trade,
    symbol: allSymbols.get(trade.symbol),
    quote,
    accountCurrency,
    symbols: allSymbols,
    quotes: allSubscribedSymbols,
  });

  // Format time
  const formatDateTime = (dateStr: string) => {
    if (dateStr === "0001-01-01T00:00:00") return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const rowBaseClass = `border-b border-border cursor-pointer hover:bg-accent transition-colors ${
    isClosing ? "opacity-50 pointer-events-none" : ""
  }`;
  const rowClassName = isSelected
    ? `${rowBaseClass} bg-accent/60`
    : rowBaseClass;

  if (!isAdvancedView) {
    // Simple view: symbol, kierunek/lots, open price -> close price, profit
    return (
      <div className={`py-2 ${rowClassName}`} onClick={onClick}>
        <div className="flex items-center gap-3">
          <SymbolAvatar name={trade.symbol} track />
          {/* Left side: Symbol, Type/Volume and Price Movement */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Top Row: Symbol, Type/Volume */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-foreground font-medium">
                {trade.symbol}
              </span>
              <span
                className={`text-sm font-medium ${
                  isBuyTrade ? "text-blue-400" : "text-red-400"
                }`}
              >
                {tradeType.toUpperCase()} {trade.lots}
              </span>
            </div>

            {/* Bottom Row: Price Movement */}
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <span>{trade.openPrice.toFixed(trade.digits)}</span>
              <span>→</span>
              <span
                className={quote ? "text-foreground" : "text-muted-foreground"}
              >
                {liveCurrentPrice.toFixed(trade.digits)}
              </span>
            </div>
          </div>

          {/* Right side: Profit centered vertically */}
          <div className="flex items-center">
            <div className="text-right">
              <p
                className={`text-base font-bold ${
                  Math.abs(liveProfit) < 0.00000001
                    ? "text-foreground"
                    : liveProfit > 0
                    ? "text-blue-400"
                    : "text-red-400"
                }`}
              >
                {formatSignedPnl(liveProfit)}
              </p>
              {isClosing && (
                <span className="text-xs text-yellow-400">Closing...</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Advanced view: pełny widok jak dotychczas
  return (
    <div className={`flex items-center gap-3 py-2 ${rowClassName}`} onClick={onClick}>
      <SymbolAvatar name={trade.symbol} track />
      <div className="min-w-0 flex-1">
      {/* Top Row: Symbol, Type/Volume, Profit */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-foreground font-medium">{trade.symbol}</span>
          <span
            className={`text-sm font-medium ${
              isBuyTrade ? "text-blue-400" : "text-red-400"
            }`}
          >
            {tradeType.toUpperCase()} {trade.lots}
          </span>
          <span className="text-xs text-muted-foreground">#{trade.ticket}</span>
        </div>
        <div className="text-right">
          <p
            className={`text-base font-bold ${
              Math.abs(liveProfit) < 0.00000001
                ? "text-foreground"
                : liveProfit > 0
                ? "text-blue-400"
                : "text-red-400"
            }`}
          >
                {formatSignedPnl(liveProfit)}
          </p>
          {isClosing && (
            <span className="text-xs text-yellow-400">Closing...</span>
          )}
        </div>
      </div>

      {/* Middle Row: Price Movement */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>{trade.openPrice.toFixed(trade.digits)}</span>
          <span>→</span>
          <span className={quote ? "text-foreground" : "text-muted-foreground"}>
            {liveCurrentPrice.toFixed(trade.digits)}
          </span>
          <span
            className={`text-xs ${
              priceChange >= 0
                ? "text-blue-400"
                : "text-red-400"
            }`}
          >
            ({priceChange >= 0 ? "+" : ""}
            {priceChange.toFixed(trade.digits)})
          </span>
        </div>
      </div>

      {/* Bottom Row: Additional Info */}
      <div className="flex items-center justify-between mt-1">
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {trade.stopLoss > 0 && (
            <span>SL: {trade.stopLoss}</span>
          )}
          {trade.takeProfit > 0 && (
            <span>TP: {trade.takeProfit}</span>
          )}
          {trade.swap !== 0 && (
            <span
              className={trade.swap >= 0 ? "text-blue-400" : "text-red-400"}
            >
              Swap: {trade.swap}
            </span>
          )}
          {(trade.commission !== 0 || trade.fee !== 0) && (
            <span className="text-orange-400">
              Comm: {(trade.commission + trade.fee).toFixed(2)}
            </span>
          )}
        </div>

        <div className="text-xs text-muted-foreground">
          Opened: {formatDateTime(trade.openTime)}
        </div>
      </div>
      </div>
    </div>
  );
}
